import { useState, memo, useCallback, useRef } from 'react'
import { useSession, useSettings, useToast } from '../context'
import { copyToClipboard, exportLogs } from '../utils'
import SessionDropdown from './SessionDropdown'

export default memo(function Toolbar({ 
  onToggleSidebar, 
  sidebarOpen, 
  onOpenSettings,
  searchTerm,
  onSearchChange,
  levelFilters,
  onLevelFilterChange,
  autoScroll,
  onToggleAutoScroll
}) {
  const { sessions, currentSessionId, selectSession, refresh, logs } = useSession()
  const { settings, setSetting } = useSettings()
  const { showToast } = useToast()
  const [filterPanelOpen, setFilterPanelOpen] = useState(false)
  const [exportMenuOpen, setExportMenuOpen] = useState(false)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [mobileScrollMenuOpen, setMobileScrollMenuOpen] = useState(false)
  const longPressTimeoutRef = useRef(null)

  const handleTouchStart = useCallback(() => {
    if (window.innerWidth >= 640) return
    longPressTimeoutRef.current = setTimeout(() => {
      setMobileScrollMenuOpen(true)
    }, 500)
  }, [])

  const handleTouchEnd = useCallback(() => {
    if (longPressTimeoutRef.current) {
      clearTimeout(longPressTimeoutRef.current)
    }
  }, [])

  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true)
    await refresh()
    setTimeout(() => setIsRefreshing(false), 500)
  }, [refresh])

  const handleCopyAll = useCallback(() => {
    if (logs.length === 0) {
      showToast('No logs to copy', 'warning')
      return
    }
    const text = logs.map(log => 
      `[${log.timestamp}] [${log.level}] ${log.message}`
    ).join('\n')
    copyToClipboard(text, showToast)
  }, [logs, showToast])

  const handleExport = useCallback((format) => {
    if (logs.length === 0) {
      showToast('No logs to export', 'warning')
      return
    }
    const filename = `${currentSessionId || 'logs'}_${new Date().toISOString().split('T')[0]}`
    exportLogs(logs, format, filename, showToast, settings.timeFormat)
    setExportMenuOpen(false)
  }, [logs, currentSessionId, showToast, settings.timeFormat])

  const handleLiveToggle = useCallback(() => {
    setSetting('liveUpdates', !settings.liveUpdates)
    showToast(settings.liveUpdates ? 'Live updates disabled' : 'Live updates enabled', 'info')
  }, [settings.liveUpdates, setSetting, showToast])

  const scrollToTop = useCallback(() => {
    document.getElementById('log-viewer')?.scrollTo({ top: 0, behavior: 'smooth' })
  }, [])

  const scrollToBottom = useCallback(() => {
    const el = document.getElementById('log-viewer')
    el?.scrollTo({ top: el.scrollHeight, behavior: 'smooth' })
  }, [])

  return (
    <header 
      className="flex-shrink-0 border-b safe-top"
      style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--color-border)' }}
    >
      {/* Main Toolbar Row */}
      <div className="flex items-center gap-2 px-3 py-2 min-h-[52px]">
        {/* Left: Menu + Navigation */}
        <div className="flex items-center gap-1.5">
          {/* Sidebar Toggle - visible on all screen sizes */}
          <IconButton
            onClick={onToggleSidebar}
            title={sidebarOpen ? "Hide sidebar (Ctrl+B)" : "Show sidebar (Ctrl+B)"}
          >
            {sidebarOpen ? <SidebarCloseIcon /> : <SidebarOpenIcon />}
          </IconButton>

          <div className="h-5 w-px mx-1 hidden sm:block" style={{ backgroundColor: 'var(--color-border)' }} />

          <div className="hidden sm:flex items-center gap-1">
            <IconButton onClick={scrollToTop} title="Scroll to top">
              <ChevronUpIcon />
            </IconButton>
            <IconButton onClick={scrollToBottom} title="Scroll to bottom">
              <ChevronDownIcon />
            </IconButton>
          </div>

          <div className="h-5 w-px mx-1 hidden sm:block" style={{ backgroundColor: 'var(--color-border)' }} />

          <div className="relative">
            <IconButton
              onClick={onToggleAutoScroll}
              onTouchStart={handleTouchStart}
              onTouchEnd={handleTouchEnd}
              onMouseDown={handleTouchStart}
              onMouseUp={handleTouchEnd}
              onMouseLeave={handleTouchEnd}
              title={`Auto-scroll: ${autoScroll ? 'ON' : 'OFF'}`}
              active={autoScroll}
            >
              <AutoScrollIcon />
            </IconButton>
            
            {mobileScrollMenuOpen && (
              <>
                <div className="fixed inset-0 z-40 sm:hidden" onClick={() => setMobileScrollMenuOpen(false)} />
                <div 
                  className="absolute top-full left-1/2 -translate-x-1/2 mt-2 p-1.5 rounded-lg border shadow-xl flex gap-2 z-50 animate-slide-up sm:hidden"
                  style={{ 
                    backgroundColor: 'var(--bg-elevated)', 
                    borderColor: 'var(--color-border)' 
                  }}
                >
                  <button
                    onClick={() => {
                      scrollToTop()
                      setMobileScrollMenuOpen(false)
                    }}
                    className="p-2 rounded hover:bg-white/10 flex items-center justify-center text-xs gap-1 font-bold font-mono"
                    style={{ color: 'var(--text-primary)' }}
                  >
                    <ChevronUpIcon />
                    <span>TOP</span>
                  </button>
                  <div className="w-px h-5 my-auto" style={{ backgroundColor: 'var(--color-border)' }} />
                  <button
                    onClick={() => {
                      scrollToBottom()
                      setMobileScrollMenuOpen(false)
                    }}
                    className="p-2 rounded hover:bg-white/10 flex items-center justify-center text-xs gap-1 font-bold font-mono"
                    style={{ color: 'var(--text-primary)' }}
                  >
                    <ChevronDownIcon />
                    <span>BOT</span>
                  </button>
                </div>
              </>
            )}
          </div>

          <IconButton
            onClick={handleLiveToggle}
            title={`Live: ${settings.liveUpdates ? 'ON' : 'OFF'}`}
            active={settings.liveUpdates}
          >
            <LiveIcon />
          </IconButton>

          <IconButton 
            onClick={handleRefresh} 
            title="Refresh"
          >
            <RefreshIcon isSpinning={isRefreshing} />
          </IconButton>
        </div>

        {/* Center: Session Selector (desktop) */}
        <div className="hidden lg:block flex-1 max-w-xs mx-4">
          <SessionDropdown
            sessions={sessions}
            currentSessionId={currentSessionId}
            onSelectSession={selectSession}
          />
        </div>

        {/* Right: Search + Actions */}
        <div className="flex items-center gap-1.5 ml-auto">
          {/* Search */}
          <div className="relative hidden sm:block">
            <SearchIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none" style={{ color: 'var(--text-muted)' }} />
            <input
              id="desktop-log-search"
              type="text"
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder="Search. (Ctrl+F)"
              className="input-field pl-8 pr-8 w-40 md:w-52"
            />
            {searchTerm && (
              <button
                onClick={() => onSearchChange('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-white/10"
                style={{ color: 'var(--text-muted)' }}
              >
                <XIcon className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          <div className="h-5 w-px mx-1 hidden md:block" style={{ backgroundColor: 'var(--color-border)' }} />

          {/* Filter */}
          <div className="relative">
            <IconButton 
              onClick={() => setFilterPanelOpen(!filterPanelOpen)}
              title="Filter"
              active={levelFilters.size < 5}
            >
              <FilterIcon />
            </IconButton>
            {filterPanelOpen && (
              <FilterPanel
                levelFilters={levelFilters}
                onLevelFilterChange={onLevelFilterChange}
                onClose={() => setFilterPanelOpen(false)}
              />
            )}
          </div>

          {/* Copy */}
          <IconButton onClick={handleCopyAll} title="Copy all">
            <CopyIcon />
          </IconButton>

          {/* Export Menu */}
          <div className="relative">
            <IconButton 
              onClick={() => setExportMenuOpen(!exportMenuOpen)}
              title="Export"
            >
              <DownloadIcon />
            </IconButton>
            {exportMenuOpen && (
              <ExportMenu 
                onExport={handleExport}
                onClose={() => setExportMenuOpen(false)}
              />
            )}
          </div>

          {/* Settings */}
          <IconButton onClick={onOpenSettings} title="Settings">
            <SettingsIcon />
          </IconButton>
        </div>
      </div>

      {/* Mobile Search Row */}
      <div className="sm:hidden px-3 pb-2">
        <div className="relative">
          <SearchIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none" style={{ color: 'var(--text-muted)' }} />
          <input
            id="mobile-log-search"
            type="text"
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search logs..."
            className="input-field pl-8 pr-8 w-full"
          />
          {searchTerm && (
            <button
              onClick={() => onSearchChange('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1"
              style={{ color: 'var(--text-muted)' }}
            >
              <XIcon className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Mobile Session Selector */}
      <div className="lg:hidden px-3 pb-2">
        <SessionDropdown
          sessions={sessions}
          currentSessionId={currentSessionId}
          onSelectSession={selectSession}
        />
      </div>
    </header>
  )
})

const IconButton = memo(function IconButton({ children, onClick, title, active, className = '', ...props }) {
  return (
    <button
      onClick={onClick}
      title={title}
      {...props}
      className={`
        flex items-center justify-center w-9 h-9 rounded-lg
        transition-all duration-150 active:scale-95
        ${active 
          ? 'bg-accent text-[var(--bg-primary)]' 
          : 'hover:bg-[var(--color-hover)] text-secondary hover:text-primary'
        }
        ${className}
      `}
      style={active ? { backgroundColor: 'var(--color-accent)', color: 'var(--bg-primary)' } : { color: 'var(--text-secondary)' }}
    >
      {children}
    </button>
  )
})

const FilterPanel = memo(function FilterPanel({ levelFilters, onLevelFilterChange, onClose }) {
  const levels = [
    { key: 'ERROR', color: 'var(--color-error)' },
    { key: 'WARN', color: 'var(--color-warn)' },
    { key: 'INFO', color: 'var(--color-info)' },
    { key: 'DEBUG', color: 'var(--color-debug)' },
    { key: 'OTHER', color: 'var(--text-muted)' }
  ]

  const handleChange = (level, checked) => {
    const newFilters = new Set(levelFilters)
    checked ? newFilters.add(level) : newFilters.delete(level)
    onLevelFilterChange(newFilters)
  }

  return (
    <>
      <div className="fixed inset-0 z-40" onClick={onClose} />
      <div 
        className="absolute top-full right-0 mt-2 p-3 rounded-lg border shadow-lg z-50 min-w-[180px] animate-slide-up"
        style={{ backgroundColor: 'var(--bg-elevated)', borderColor: 'var(--color-border)' }}
      >
        <div className="text-xs font-semibold uppercase mb-3 tracking-wide" style={{ color: 'var(--text-muted)' }}>
          Log Levels
        </div>
        <div className="space-y-2">
          {levels.map(({ key, color }) => (
            <label key={key} className="flex items-center gap-3 cursor-pointer group">
              <input
                type="checkbox"
                checked={levelFilters.has(key)}
                onChange={(e) => handleChange(key, e.target.checked)}
                className="w-4 h-4 rounded"
                style={{ accentColor: color }}
              />
              <span className="text-sm flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
                {key}
              </span>
            </label>
          ))}
        </div>
      </div>
    </>
  )
})

const ExportMenu = memo(function ExportMenu({ onExport, onClose }) {
  const formats = [
    { key: 'txt', label: 'Text (.txt)', icon: '📄' },
    { key: 'json', label: 'JSON (.json)', icon: '{ }' },
    { key: 'csv', label: 'CSV (.csv)', icon: '📊' }
  ]

  return (
    <>
      <div className="fixed inset-0 z-40" onClick={onClose} />
      <div 
        className="absolute top-full right-0 mt-2 py-1 rounded-lg border shadow-lg z-50 min-w-[160px] animate-slide-up"
        style={{ backgroundColor: 'var(--bg-elevated)', borderColor: 'var(--color-border)' }}
      >
        {formats.map(({ key, label, icon }) => (
          <button
            key={key}
            onClick={() => onExport(key)}
            className="w-full px-3 py-2 text-left text-sm flex items-center gap-2 transition-colors hover:bg-[var(--color-hover)]"
            style={{ color: 'var(--text-primary)' }}
          >
            <span className="text-xs opacity-70">{icon}</span>
            {label}
          </button>
        ))}
      </div>
    </>
  )
})

// Icons
const SidebarOpenIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
    <path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z" />
  </svg>
)

const SidebarCloseIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
    <path d="M3 18h13v-2H3v2zm0-5h10v-2H3v2zm0-7v2h13V6H3zm18 9.59L17.42 12 21 8.41 19.59 7l-5 5 5 5L21 15.59z" />
  </svg>
)

const ChevronUpIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
    <path d="M7.41 15.41L12 10.83l4.59 4.58L18 14l-6-6-6 6z" />
  </svg>
)

const ChevronDownIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
    <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z" />
  </svg>
)

const AutoScrollIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
    <path d="M16 17.01V10h-2v7.01h-3L15 21l4-3.99h-3zM9 3L5 6.99h3V14h2V6.99h3L9 3z" />
  </svg>
)

const LiveIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
    <circle cx="12" cy="12" r="8" fill="none" stroke="currentColor" strokeWidth="2" />
    <circle cx="12" cy="12" r="3" />
  </svg>
)

const RefreshIcon = ({ isSpinning }) => (
  <svg 
    viewBox="0 0 24 24" 
    fill="currentColor" 
    className={`w-5 h-5 ${isSpinning ? 'animate-spin-icon' : ''}`}
  >
    <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z" />
  </svg>
)

const SearchIcon = ({ className, style }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} style={style}>
    <path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
  </svg>
)

const XIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
  </svg>
)

const FilterIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
    <path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z" />
  </svg>
)

const CopyIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
    <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z" />
  </svg>
)

const DownloadIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
    <path d="M19 12v7H5v-7H3v7c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2v-7h-2zm-6 .67l2.59-2.58L17 11.5l-5 5-5-5 1.41-1.41L11 12.67V3h2z" />
  </svg>
)

const SettingsIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
    <path d="M19.14 12.94c.04-.3.06-.61.06-.94 0-.32-.02-.64-.07-.94l2.03-1.58c.18-.14.23-.41.12-.61l-1.92-3.32c-.12-.22-.37-.29-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94L14.4 2.81c-.04-.24-.24-.41-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.05.3-.09.63-.09.94s.02.64.07.94l-2.03 1.58c-.18.14-.23.41-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z" />
  </svg>
)
